import NoMachinesEmptyState from 'app/components/access-sharing/EquipmentAndFleets/NoMachinesEmptyState';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import {
  AccessSharingFlowProvider,
  useAccessSharingFlowContext,
} from 'app/hooks/access-sharing';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FleetSelectionStepController } from './components';
import { ACCESS_SHARING_FLEET_STEPS } from 'app/constants/access-sharing';
import { ConfirmNavigateAwayDialog } from 'app/components/dialogs';
import useNavigationPrompt from 'app/hooks/useNavigationPrompt';
import {
  CreateFleetStepController,
  FleetAccessSharingStepController,
  FleetCreatedStepController,
  FleetSharingOverviewStepController,
} from 'app/components/access-sharing/flows';
import { useIsMobile } from 'app/hooks/useIsMobile';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';

export default function ShareFleetPage() {
  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  const isMobile = useIsMobile();
  // TODO throw it away as soon as possible
  // Refactor layout components in the future to be more flexible
  // and avoid hacks like this.
  React.useEffect(() => {
    let classNameIndex = -1;

    if (!isMobile) {
      classNameIndex = setClassNames('layout-grey');
    }
    return () => {
      if (classNameIndex >= 0) {
        removeClassNames(classNameIndex);
        classNameIndex = -1;
      }
    };
  }, [isMobile]);

  return (
    <AccessSharingFlowProvider
      stepCount={Object.keys(ACCESS_SHARING_FLEET_STEPS).length}
      // startStep={ACCESS_SHARING_FLEET_STEPS.OVERVIEW}
    >
      <StepRenderer />
      <UnsavedChangesWarning />
    </AccessSharingFlowProvider>
  );
}

function StepRenderer() {
  const navigate = useNavigate();
  const { stepper } = useAccessSharingFlowContext();
  const { step, setStep } = stepper;
  const { t } = useTranslation();

  function handleBack() {
    navigate('/access/equipment-and-fleets');
  }
  const { equipments, sharesData } = useGetEquipmentData();

  const orderedFleets = useMemo(() => {
    if (!sharesData?.fleets) return [];
    return orderBy(
      sharesData?.fleets,
      'sharedItemDetails.sharedItemLabel',
      'asc',
    );
  }, [sharesData?.fleets]);

  const hasAtLeastOneFleet = useMemo(() => {
    return Boolean(orderedFleets?.length);
  }, [orderedFleets]);

  if (!equipments?.length) {
    return <NoMachinesEmptyState onClose={() => handleBack()} />;
  }

  switch (step) {
    case ACCESS_SHARING_FLEET_STEPS.FLEET_SELECTION:
      if (!hasAtLeastOneFleet)
        setStep(ACCESS_SHARING_FLEET_STEPS.FLEET_CREATION_NO_FLEETS);
      return (
        <FleetSelectionStepController
          fleets={orderedFleets}
          onBack={handleBack}
        />
      );
    case ACCESS_SHARING_FLEET_STEPS.FLEET_CREATION:
      return (
        <CreateFleetStepController
          equipment={equipments}
          hasAtLeastOneFleet={Boolean(orderedFleets?.length)}
          onBack={handleBack}
        />
      );
    case ACCESS_SHARING_FLEET_STEPS.FLEET_CREATION_NO_FLEETS:
      return (
        <CreateFleetStepController
          equipment={equipments}
          hasAtLeastOneFleet={false}
          onBack={handleBack}
          title={t('create_fleet_no_fleets_title')}
          subtitle={t('create_fleet_no_fleets_subtitle')}
        />
      );
    case ACCESS_SHARING_FLEET_STEPS.FLEET_CREATED:
      return <FleetCreatedStepController onBack={handleBack} />;
    case ACCESS_SHARING_FLEET_STEPS.ACCESS_SHARING:
      return <FleetAccessSharingStepController />;
    case ACCESS_SHARING_FLEET_STEPS.OVERVIEW:
      return (
        <FleetSharingOverviewStepController
          fleets={orderedFleets}
          onBack={handleBack}
        />
      );
    default:
      return null;
  }
}

function UnsavedChangesWarning() {
  const { isSubmitted, isEditing, selectedUsers, selectedFleetId, stepper } =
    useAccessSharingFlowContext();
  const { step } = stepper;

  const isDirty = selectedUsers.size > 0 || !!selectedFleetId;
  const isStepWithPrompt =
    step === ACCESS_SHARING_FLEET_STEPS.FLEET_CREATED ||
    step === ACCESS_SHARING_FLEET_STEPS.OVERVIEW;

  const { showPrompt, confirmNavigation, cancelNavigation } =
    useNavigationPrompt(
      isDirty && (isStepWithPrompt || isEditing) && !isSubmitted,
    );

  return (
    <ConfirmNavigateAwayDialog
      open={showPrompt}
      onConfirm={confirmNavigation}
      onClose={cancelNavigation}
    />
  );
}
